import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Tournament {
    clave: string | number;
    nombre: string;
    fechaInicio: string;
    // Add other tournament properties if needed
}

const Welcome: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const fromDashboard = location.state?.fromDashboard;
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${BACKEND_URL}/tournaments`)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then(data => {
                
                setTournaments(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(err => {
                console.error('❌ Error fetching tournaments:', err);
                setTournaments([]);
                setLoading(false);
            });
    }, []);

    const handleCardClick = (tournament: Tournament) => {
        // Check if user is logged in (coming from dashboard)
        const loggedInPlayerStr = localStorage.getItem('loggedInPlayer');
        
        if (fromDashboard && loggedInPlayerStr) {
            // User is logged in, skip TournamentDetail and go directly to TurnosSignUp
            const jugador = JSON.parse(loggedInPlayerStr);
            navigate('/turnos-signup', { state: { jugador, tournament } });
        } else {
            // User is not logged in, go to TournamentDetail for player search/registration
            navigate('/tournament', { state: { tournament } });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-gray-600">Loading tournaments...</div>
            </div>
        );
    }

    const player = fromDashboard ? JSON.parse(localStorage.getItem('loggedInPlayer') || '{}') : null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-2xl border-b-4 border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="bg-gray-50 rounded-2xl p-3 shadow-inner">
                                <img 
                                    src="/logo.avif" 
                                    alt="Logo" 
                                    className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                                />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                    {player ? player.NOMBRECOMPLETO : 'Select Your Tournament'}
                                </h1>
                                {player && (
                                    <>
                                        <p className="text-gray-600 text-sm sm:text-base">TAT ID: {player.FICHANAL}</p>
                                        <p className="text-gray-600 text-sm sm:text-base">{player.EMAIL} | {player.CITY}, {player.ESTADOCORTO}</p>
                                    </>
                                )}
                                {!player && (
                                    <p className="text-gray-600 text-sm sm:text-base">Choose an event to register and compete</p>
                                )}
                            </div>
                        </div>
                        {fromDashboard && (
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="self-start sm:self-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Dashboard
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center mb-8">
                
                <p className="text-base sm:text-lg text-gray-600">Choose an event to register and compete</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full max-w-5xl mx-auto">
                {tournaments.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-gray-600">
                        No tournaments available at this time.
                    </div>
                ) : (
                    tournaments.map(t => (
                    <div
                        key={t.clave}
                        className="bg-white shadow rounded-lg p-4 cursor-pointer hover:bg-blue-100 transition-transform h-44 flex flex-col justify-between w-full"
                        onClick={() => handleCardClick(t)}
                    >
                        <h2 className="text-lg font-semibold text-red-600 mb-1">{t.nombre}</h2>
                        <p className="text-gray-600 mb-2">Start Date: {new Date(t.fechaInicio).toLocaleDateString()}</p>
                    </div>
                    ))
                )}
                </div>
            </div>
        </div>
    );
};

export default Welcome;